// Sidebar and TopNav removed

import {
  Box,
  Button,
  Chip,
  Grid,
  Stack,
  Typography,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material'
import DetailRow from '../../common/DetailRow'
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab'
import { timelineItemClasses } from '@mui/lab/TimelineItem';
import Link from '@mui/material/Link'
// Icons are locally stubbed below to avoid external icon package dependency
import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAppSelector, useAppDispatch } from '../../../../../store'
import type { Lead } from '../../../types'
import { updateLead, fetchLeadById, convertLead, selectSelectedLead, selectLeadLoading, selectLeadError } from '../../../store/slices/leadSlice'
import FollowupModal from './FollowupModal'
import ScheduleAppointmentModal from './ScheduleAppointmentModal'
import AddLeadModal from './AddLeadModal'
import CreateContactModal from '../Contact/CreateContactModal'
import { ArrowBack } from '@mui/icons-material'
import { CheckIcon } from '@heroicons/react/24/outline';
import {
  getStepperStageFromDetailed,
  getStepperStageIndexDynamic,
  getDynamicStepperStages,
  getSubStatusColor,
  getStageVisibility,
  isTerminalStage,
  type DetailedStage,
} from '../../../config/stageConfig'
import { formatDate, formatDateTime } from '../../../../../utils/formatters'
import { useLeadStages } from '../../../hooks/useLeadStages'
import { capitalizeWords } from '../../../../../utils/formatters'

// Local icon placeholders to avoid external icon package dependency
// These keep the JSX structure without requiring @mui/icons-material
const IconPlaceholder: React.FC<any> = () => null

const ArrowBackIcon = IconPlaceholder
const PhoneIcon = IconPlaceholder
const DescriptionIcon = IconPlaceholder
const StraightenIcon = IconPlaceholder
const EmailIcon = IconPlaceholder
const WhatsAppIcon = IconPlaceholder
const AddIcon = IconPlaceholder
const PersonIcon = IconPlaceholder
const InfoIcon = IconPlaceholder
const StorefrontIcon = IconPlaceholder
const CalendarTodayIcon = IconPlaceholder
const LocationOnIcon = IconPlaceholder
const PictureAsPdfIcon = IconPlaceholder
const CameraAltIcon = IconPlaceholder
const PeopleIcon = IconPlaceholder
const EditIcon = IconPlaceholder
const SendIcon = IconPlaceholder
const MoreVertIcon = IconPlaceholder

// Helpful type aliases for mapped collections
type FollowUpActivity = NonNullable<Lead['followUpHistory']>[number]
type LeadMeasurement = NonNullable<Lead['measurements']>[number]
type MeasurementPhoto = NonNullable<
  NonNullable<Lead['measurements']>[number]['sitePhotos']
>[number]
type LeadQuotation = NonNullable<Lead['quotations']>[number]

// Format the title shown in the communication timeline for follow-ups
const formatFollowUpTitle = (activity: FollowUpActivity): string => {
  const channel = activity.channel || ''

  // If this is a follow-up logged via contact method, show
  // "Follow-up Call / WhatsApp / Mail" explicitly
  const channelLower = channel.toLowerCase()
  const isFollowUpChannel =
    channelLower.startsWith('follow-up') || channelLower.startsWith('follow up')

  if (isFollowUpChannel) {
    if (activity.type === 'phone') {
      return 'Follow-up Call'
    }
    if (activity.type === 'whatsapp') {
      return 'Follow-up WhatsApp'
    }
    if (activity.type === 'email') {
      return 'Follow-up Mail'
    }
    // Fallback: use the channel text as-is
    return channel
  }

  // For site/store visits and other stage / next-action based activities,
  // use the channel text directly (e.g. "Site Visit", "Store Visit",
  // "Measurement Done", "Quotation Sent", etc.)
  if (channel) {
    return channel
  }

  return 'Activity'
}

export default function LeadDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const lead = useAppSelector(selectSelectedLead);
  const loading = useAppSelector(selectLeadLoading);
  const error = useAppSelector(selectLeadError);

  const [isFollowupModalOpen, setIsFollowupModalOpen] = useState(false)
  const [isScheduleAppointmentModalOpen, setIsScheduleAppointmentModalOpen] = useState(false)
  const [isEditLeadModalOpen, setIsEditLeadModalOpen] = useState(false)
  const [isCreateContactModalOpen, setIsCreateContactModalOpen] = useState(false)
  const [rescheduleType, setRescheduleType] = useState<'storeVisit' | 'siteVisit' | null>(null)

  // Dynamic stage hook
  const { stages: dynamicStages, getStageByKey, getStageOrder, getStageByOrder, isOptional: isOptionalStage } = useLeadStages();

  // Handle follow-up entry + optional stage update from CreateContactModal
  const handleFollowUpEntry = async (payload: {
    followUpEntry: NonNullable<Lead['followUpHistory']>[number];
    stageUpdate?: {
      detailedStage: DetailedStage;
      stageSubStatus?: 'scheduled' | 'completed' | 'send' | 'approved';
    };
  }) => {
    if (!lead) return

    const { followUpEntry, stageUpdate } = payload

    // Always prepend the new follow-up entry to the timeline
    let updatedLead: Lead = {
      ...lead,
      followUpHistory: [
        followUpEntry,
        ...(lead.followUpHistory || []),
      ],
    }

    // If there's a stage update, apply it to the lead
    if (stageUpdate) {
      // Track when optional stages are first selected
      const newStage = stageUpdate.detailedStage;
      const isOptStage = isOptionalStage(newStage);

      // Update stageSelectedAt if this is an optional stage and it's being selected for the first time
      let updatedStageSelectedAt = updatedLead.stageSelectedAt || {};
      if (isOptStage && !updatedStageSelectedAt[newStage]) {
        updatedStageSelectedAt = {
          ...updatedStageSelectedAt,
          [newStage]: new Date().toISOString(),
        };
      }

      // Update the lead with the new stage
      const stageId = getStageOrder(newStage);

      updatedLead = {
        ...updatedLead,
        stageId,
        detailedStage: newStage,
        stageSubStatus: stageUpdate.stageSubStatus,
        stageSelectedAt: Object.keys(updatedStageSelectedAt).length > 0 ? updatedStageSelectedAt : undefined,
        currentStage: newStage,
        stage: newStage,
      }
    }

    // Sync with Redux store so list & detail stay in sync
    await dispatch(updateLead({ id: updatedLead.id, data: updatedLead }))

    // Re-fetch the lead to ensure we have the latest from server
    // (the PUT response now includes followUpHistory, but this is a safety net)
    if (id) {
      dispatch(fetchLeadById(id))
    }
  }

  // Apply stage update coming from timeline action buttons
  const applyStageUpdateFromTimeline = async (update: {
    detailedStage: DetailedStage;
    stageSubStatus?: 'scheduled' | 'completed' | 'send' | 'approved';
  }) => {
    if (!lead) return

    // Track when optional stages are first selected
    // Track when optional stages are first selected
    const newStage = update.detailedStage;
    const isOptStage = isOptionalStage(newStage);

    // Update stageSelectedAt if this is an optional stage and it's being selected for the first time
    let updatedStageSelectedAt = lead.stageSelectedAt || {};
    if (isOptStage && !updatedStageSelectedAt[newStage]) {
      updatedStageSelectedAt = {
        ...updatedStageSelectedAt,
        [newStage]: new Date().toISOString(),
      };
    }

    const stageId = getStageOrder(newStage);

    const updatedLead: Lead = {
      ...lead,
      stageId,
      detailedStage: newStage,
      stageSubStatus: update.stageSubStatus,
      stageSelectedAt: Object.keys(updatedStageSelectedAt).length > 0 ? updatedStageSelectedAt : undefined,
      currentStage: newStage,
      stage: newStage,
    }

    await dispatch(updateLead({ id: updatedLead.id, data: updatedLead }))

    // Re-fetch to ensure complete data from server
    if (id) {
      dispatch(fetchLeadById(id))
    }
  }

  const handleConvertToCustomer = async () => {
    if (!lead) return;
    try {
      // Get contact person name from lead (prioritize contact.name, then name field, then contactPersonName)
      const contactPersonName = lead.contact?.name || lead.name || lead.contact?.contactPersonName || '';
      
      // Call convert API
      const result = await dispatch(convertLead({ id: lead.id, contactPersonName })).unwrap();
      console.log("result===>", result);
      
      // If lead is not in a terminal stage (won/lost/hold), also update the stage to "won"
      const currentDetailedStage = lead.detailedStage || lead.currentStage || lead.stage;
      if (currentDetailedStage && !isTerminalStage(currentDetailedStage)) {
        const wonStageId = getStageOrder('won');
        const now = new Date();
        
        // Create a follow-up history entry for "Won" stage to show in communication timeline
        const wonFollowUpEntry = {
          id: `followup-${Date.now()}`,
          type: 'followup' as const,
          channel: 'Won',
          date: now.toISOString(),
          summary: 'Lead converted to order - Stage updated to Won',
          outcome: 'Completed',
          outcomeColor: 'success' as const,
          owner: 'Current User', // TODO: Get from user context
          ownerInitials: 'CU',
          createdAt: now.toISOString(),
        };
        
        // Prepend the new follow-up entry to the timeline
        const updatedFollowUpHistory = [
          wonFollowUpEntry,
          ...(lead.followUpHistory || []),
        ];
        
        const updatedLeadData: Partial<Lead> = {
          stageId: wonStageId,
          detailedStage: 'won' as DetailedStage,
          currentStage: 'won',
          stage: 'won',
          followUpHistory: updatedFollowUpHistory,
        };
        
        await dispatch(updateLead({ id: lead.id, data: updatedLeadData })).unwrap();
        
        // Refetch the lead to ensure UI is in sync with the updated follow-up history
        if (id) {
          await dispatch(fetchLeadById(id)).unwrap();
        }
      }
      
      toast.success('Lead converted to order successfully!');
      // Navigate back to leads list after successful conversion
      // navigate('/leads', { state: { activeMenu: 'enquiry' } });
    } catch (err: any) {
      console.error('Failed to convert lead:', err);
      toast.error(err || 'Failed to convert lead');
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchLeadById(id))
    }
  }, [id, dispatch])

  // Get activity feed items for Follow-up History
  const getActivityFeedItems = () => {
    if (!lead || !lead.followUpHistory || lead.followUpHistory.length === 0) {
      return []
    }

    // Map follow-up history into compact activity feed items (title + time + icon/color)
    return lead.followUpHistory.map((activity: FollowUpActivity) => {
      let icon = <PhoneIcon sx={{ fontSize: 16 }} />
      let color: 'primary' | 'success' | 'info' | 'warning' | 'error' = 'primary'

      switch (activity.type) {
        case 'phone':
        case 'followup':
          icon = <PhoneIcon sx={{ fontSize: 16 }} />
          color = 'primary'
          break
        case 'whatsapp':
          icon = <WhatsAppIcon sx={{ fontSize: 16 }} />
          color = 'success'
          break
        case 'email':
          icon = <EmailIcon sx={{ fontSize: 16 }} />
          color = 'info'
          break
        case 'siteVisit':
          icon = <LocationOnIcon sx={{ fontSize: 16 }} />
          color = 'warning'
          break
        case 'storeVisit':
          icon = <StorefrontIcon sx={{ fontSize: 16 }} />
          color = 'warning'
          break
        default:
          icon = <PhoneIcon sx={{ fontSize: 16 }} />
          color = 'primary'
      }

      return {
        icon,
        color,
        title: formatFollowUpTitle(activity),
        time: activity.date ? new Date(activity.date).toLocaleString() : '',
      }
    })
  }

  useEffect(() => {
    console.log('getActivityFeedItems:',getActivityFeedItems());
  }, [getActivityFeedItems]);

  const getBreadcrumbs = () => {
    if (lead) {
      return `Home > Leads > ${lead.contact?.name || ''}`;
    }
    return 'Home > Leads > Lead Detail';
  };

  const renderContent = () => {
    if (loading && !lead) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/', { state: { activeMenu: 'enquiry' } })}
            sx={{ mb: 2 }}
          >
            Back to Leads
          </Button>
        </Box>
      );
    }

    if (!lead) {
      return (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Lead not found
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/', { state: { activeMenu: 'enquiry' } })}
            sx={{ mb: 2 }}
          >
            Back to Leads
          </Button>
        </Box>
      );
    }

    return (
      <Box>

        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', md: 'flex-start' },
            gap: { xs: 2, md: 2 },
            mb: 2 
          }}
        >
          {/* Left Section: Back Button + Lead Info */}
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'row', 
              gap: 1, 
              flex: 1, 
              minWidth: 0, 
              width: { xs: '100%', md: 'auto' },
              alignItems: 'flex-start'
            }}
          >
            <IconButton
              onClick={() => navigate('/leads', { state: { activeMenu: 'enquiry' } })}
              sx={{ color: "text.secondary", flexShrink: 0, mt: { xs: 0, md: 0.5 } }}
            >
              <ArrowBack />
            </IconButton>
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: { xs: 1, sm: 1.5 },
                flex: 1,
                minWidth: 0,
                width: '100%'
              }}
            >
              {/* Lead Name - Full width, wraps naturally */}
              <Typography 
                variant="h5" 
                fontWeight={700}
                sx={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  lineHeight: 1.3,
                  fontSize: { xs: '1.25rem', sm: '1.5rem' }
                }}
              >
                {capitalizeWords(lead.contact?.name) || ''}
              </Typography>
              
              {/* Metadata Row: Date, Address, Chip */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 1, sm: 2 },
                  flexWrap: 'wrap'
                }}
              >
                <Typography 
                  color="text.secondary" 
                  fontSize={{ xs: 13, sm: 14 }}
                  sx={{
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word'
                  }}
                >
                  {lead.createdAt
                    ? `Created ${new Date(lead.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                    : 'Created date not available'
                  }
                  {lead.contact?.address && (
                    <>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}> · </Box>
                      <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>
                        {lead.contact.address}
                      </Box>
                    </>
                  )}
                </Typography>
                {/* <Box sx={{ flexShrink: 0 }}>
                  <Chip
                    label={`HIGH QUALITY: ${lead.score}/100`}
                    color="success"
                    size="small"
                  />
                </Box> */}
              </Box>
            </Box>
          </Box>
          
          {/* Right Section: Action Buttons */}
          <Box 
            sx={{ 
              display: 'flex', 
              gap: 2,
              flexShrink: 0,
              width: { xs: '100%', md: 'auto' },
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
              flexWrap: 'wrap',
              alignItems: 'flex-start',
              mt: { xs: 0, md: 0.5 }
            }}
          >
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setIsEditLeadModalOpen(true)}
              sx={{ 
                minWidth: { xs: 'auto', sm: '100px' },
                whiteSpace: 'nowrap'
              }}
            >
              Edit
            </Button>
     
            {/* Convert to Customer Button */}
            {lead && 
             lead?.detailedStage !== 'lost' && 
             lead?.detailedStage !== 'hold' && (
              <Button
                variant="contained"
                color="success"
                onClick={handleConvertToCustomer}
                disabled={
                  (lead.status?.toLowerCase() === 'converted') ||
                  !(
                    (lead?.detailedStage === 'quotation' && lead?.stageSubStatus === 'approved') ||
                    lead?.detailedStage === 'won'
                  )
                }
                sx={{ 
                  minWidth: { xs: 'auto', sm: '150px' },
                  whiteSpace: 'nowrap'
                }}
              >
                Convert to Order
              </Button>
            )}
          </Box>
        </Box>

        {/* Header */}
        <Card sx={{ mb: 2, width: '100%' }}>
          <CardContent sx={{ p: 2 }}>
            <Grid container spacing={4} alignItems="center" flex='1' flexDirection='row'>
              <Grid size={12}>
                {/* Stepper Bar */}
                {(() => {
                  // Get current detailed stage from lead (prioritize stageId over string fields)
                  let detailedStageValue = lead.stageId
                    ? (getStageByOrder(lead.stageId)?.key || 'new')
                    : (lead.detailedStage || lead.currentStage || lead.stage || 'new').trim();

                  // Find matching stage (case-insensitive) to get the correct camelCase key
                  const matchingStage = dynamicStages.find(
                    (s) => s.key.toLowerCase() === detailedStageValue.toLowerCase()
                  );

                  // Use the correct camelCase key from config, or fallback to the original value
                  const detailedStage = (matchingStage?.key || detailedStageValue) as DetailedStage;
                  const stageSubStatus = lead.stageSubStatus;

                  // Map detailed stage to stepper stage
                  const currentStepperStage = getStepperStageFromDetailed(detailedStage);

                  // Get dynamically ordered stepper stages based on follow-up history
                  // Filter to only include visible stages
                  const dynamicStepperStages = getDynamicStepperStages(
                    lead.followUpHistory,
                    detailedStage,
                    stageSubStatus,
                    lead.stageSelectedAt
                  ).filter(stepperStage => {
                    // Always show enquiry stage (first stage - maps to 'new' detailed stage)
                    if (stepperStage.key === 'enquiry') {
                      return true;
                    }
                    // Check if any detailed stage for this stepper stage is visible
                    const detailedStagesForStepper = dynamicStages.filter(
                      ds => {
                        // Match by stepperLabel, label, or key
                        const matchesStepperLabel = (ds.stepperLabel || ds.label) === stepperStage.label;
                        const matchesKey = ds.key === stepperStage.key;
                        // Also check if the stage key is 'new' for enquiry stepper stage
                        const isNewStageForEnquiry = stepperStage.key === 'enquiry' && ds.key === 'new';
                        return matchesStepperLabel || matchesKey || isNewStageForEnquiry;
                      }
                    );
                    return detailedStagesForStepper.some(ds =>
                      getStageVisibility(ds.key, lead.followUpHistory, detailedStage, lead.stageSelectedAt)
                    );
                  });

                  // Get current step index using dynamic ordering
                  const currentStepIndex = getStepperStageIndexDynamic(currentStepperStage, dynamicStepperStages);

                  // Determine if current stage is actually completed based on sub-status
                  // If sub-status is 'scheduled' or 'send', the stage is not completed yet
                  // Only 'completed' or 'approved' means the stage is done
                  const isCurrentStageCompleted =
                    !stageSubStatus || // No sub-status means stage is completed
                    stageSubStatus === 'completed' ||
                    stageSubStatus === 'approved' ||
                    (currentStepperStage === 'designFinalized'); // Design finalized is always completed

                  return (
                    <Stepper
                      activeStep={isCurrentStageCompleted ? currentStepIndex + 1 : currentStepIndex}
                      alternativeLabel
                      sx={{
                        mb: 2,
                        '& .MuiStepConnector-root': {
                          top: 20,
                          left: 'calc(-50% + 20px)',
                          right: 'calc(50% + 20px)',
                        },
                        '& .MuiStepConnector-line': {
                          borderTopWidth: 2,
                          borderColor: '#E0E0E0',
                        },
                        '& .MuiStepConnector-active .MuiStepConnector-line': {
                          borderColor: '#D4F8E8',
                        },
                        '& .MuiStepConnector-completed .MuiStepConnector-line': {
                          borderColor: '#D4F8E8',
                        },
                      }}
                    >
                      {dynamicStepperStages.map((step, index) => {
                        // A stage is completed if:
                        // 1. It's before the current step (always completed - previous stages stay green)
                        // 2. It's the current step AND the current stage is completed
                        const isCompleted = index < currentStepIndex || (index === currentStepIndex && isCurrentStageCompleted);
                        const isCurrent = index === currentStepIndex;
                        const isPending = index > currentStepIndex;

                        // Get sub-status color if applicable
                        let stepColor = '#424242'; // Default dark gray
                        let stepBgColor = '#F5F5F5'; // Default light gray
                        let stepBorderColor = '#E0E0E0';

                        if (isCurrent && step.supportsSubStatus && stageSubStatus) {
                          const subStatusColor = getSubStatusColor(step.key, stageSubStatus);
                          if (subStatusColor) {
                            stepColor = subStatusColor.color;
                            stepBgColor = subStatusColor.bgColor;
                            stepBorderColor = subStatusColor.color;
                          }
                        } else if (isCurrent && !step.supportsSubStatus) {
                          // Current stage without sub-status (like designFinalized) - show as completed/green
                          stepColor = '#2E7D32'; // Green
                          stepBgColor = '#D4F8E8'; // Light green
                        } else if (isCompleted) {
                          stepColor = '#2E7D32'; // Green
                          stepBgColor = '#D4F8E8'; // Light green
                        } else if (isCurrent) {
                          stepColor = '#FFFFFF';
                          stepBgColor = '#424242'; // Dark gray
                        }

                        // Custom StepIcon component
                        const StepIcon = (props: any) => {
                          const { active, completed } = props;
                          const isStepCompleted = completed || (active && isCurrentStageCompleted);
                          const isStepCurrent = active && !isStepCompleted;

                          return (
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: isStepCompleted ? '#D4F8E8' : isStepCurrent ? stepBgColor : '#F5F5F5',
                                border: isStepCompleted ? '2px solid #2E7D32' : isPending ? `1px solid ${stepBorderColor}` : isStepCurrent && step.supportsSubStatus && stageSubStatus ? `2px solid ${stepColor}` : 'none',
                              }}
                            >
                              {isStepCompleted ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <CheckIcon style={{ width: 20, height: 20, color: 'black' }} />
                                </Box>
                              ) : (
                                <Typography
                                  sx={{
                                    fontSize: 16,
                                    fontWeight: 600,
                                    color: isStepCurrent ? stepColor : '#9E9E9E',
                                  }}
                                >
                                  {index + 1}
                                </Typography>
                              )}
                            </Box>
                          );
                        };

                        return (
                          <Step key={step.key} completed={isCompleted} active={isCurrent}>
                            <StepLabel
                              StepIconComponent={StepIcon}
                              sx={{
                                '& .MuiStepLabel-label': {
                                  mt: 1,
                                },
                              }}
                            >
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    fontWeight: isCurrent ? 600 : 400,
                                    color: isPending ? '#9E9E9E' : '#424242',
                                    textAlign: 'center',
                                  }}
                                >
                                  {step.displayLabel}
                                </Typography>
                                {isCurrent && step.supportsSubStatus && stageSubStatus && (
                                  <Typography
                                    sx={{
                                      fontSize: 10,
                                      fontWeight: 500,
                                      color: stepColor,
                                      textAlign: 'center',
                                      mt: 0.5,
                                    }}
                                  >
                                    {getSubStatusColor(step.key, stageSubStatus)?.label || stageSubStatus}
                                  </Typography>
                                )}
                              </Box>
                            </StepLabel>
                          </Step>
                        );
                      })}
                    </Stepper>
                  );
                })()}
              </Grid>

            </Grid>
          </CardContent>
        </Card>

        {/* Main Content */}
        <Grid container spacing={3} justifyContent='space-between'>
          {/* Left Section */}
          <Grid size={8}>
            {/* Follow-up History */}
            {(
              <Card>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={3}
                  >
                    <Typography variant="h6" fontWeight={600}>
                      Communication Timeline
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setRescheduleType(null)
                        setIsCreateContactModalOpen(true)
                      }}
                      disabled={lead?.detailedStage === 'lost'}
                    >
                      Log New Activity
                    </Button>
                  </Box>

                  {!lead?.followUpHistory || lead.followUpHistory.length === 0 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                      <Typography color="text.secondary">
                        No follow-up history available
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ position: 'relative', pl: 4 }}>
                      {/* Continuous vertical timeline line */}
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 15,
                          top: 0,
                          bottom: 0,
                          width: 2,
                          bgcolor: 'primary.main',
                        }}
                      />

                      <Stack spacing={3}>
                        {[...(lead.followUpHistory || [])]
                          .sort((a, b) => {
                            // Sort primarily by createdAt (when activity was logged) in descending order (newest first)
                            const createdA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                            const createdB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                            
                            // If createdAt timestamps are different, sort by createdAt
                            if (createdA !== createdB) {
                              return createdB - createdA; // Descending order (newest first)
                            }
                            
                            // If createdAt is equal or not available, fall back to date field
                            const dateA = a.date ? new Date(a.date).getTime() : 0;
                            const dateB = b.date ? new Date(b.date).getTime() : 0;
                            return dateB - dateA; // Descending order (newest first)
                          })
                          .map((activity: FollowUpActivity) => (
                          <Box
                            key={activity.id}
                            display="flex"
                            alignItems="flex-start"
                            sx={{ position: 'relative' }}
                          >
                            {/* Timeline node with colored icon background */}
                            <Box
                              sx={{
                                position: 'absolute',
                                left: -32,
                                top: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                bgcolor:
                                  activity.type === 'phone'
                                    ? 'primary.light'
                                    : activity.type === 'whatsapp'
                                      ? '#D4F8E8'
                                      : activity.type === 'email'
                                        ? '#E8D5F5'
                                        : activity.type === 'siteVisit'
                                          ? '#FFF3E0'
                                          : activity.type === 'storeVisit'
                                            ? '#E3F2FD'
                                            : '#F3E5F5',
                                zIndex: 1,
                              }}
                            >
                              {activity.type === 'phone' && (
                                <PhoneIcon
                                  fontSize="small"
                                  sx={{ color: 'primary.main' }}
                                />
                              )}
                              {activity.type === 'whatsapp' && (
                                <WhatsAppIcon
                                  fontSize="small"
                                  sx={{ color: '#25D366' }}
                                />
                              )}
                              {activity.type === 'email' && (
                                <EmailIcon
                                  fontSize="small"
                                  sx={{ color: 'secondary.main' }}
                                />
                              )}
                              {activity.type === 'siteVisit' && (
                                <LocationOnIcon
                                  fontSize="small"
                                  sx={{ color: '#F57C00' }}
                                />
                              )}
                              {activity.type === 'storeVisit' && (
                                <StorefrontIcon
                                  fontSize="small"
                                  sx={{ color: '#1976D2' }}
                                />
                              )}
                              {activity.type === 'followup' && (
                                <PhoneIcon
                                  fontSize="small"
                                  sx={{ color: '#7B1FA2' }}
                                />
                              )}
                            </Box>

                            {/* Activity card */}
                            <Card
                              variant="outlined"
                              sx={{
                                flex: 1,
                                boxShadow: 'none',
                                border: '1px solid',
                                borderColor: 'divider',
                              }}
                            >
                              <Box sx={{ padding: 1 }}>
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <Box display="flex" flexDirection="column" gap={0.5}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Typography fontWeight={600} variant="h6">
                                        {formatFollowUpTitle(activity)}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Box display="flex" flexDirection="column" alignItems="flex-end">
                                    
                                    <DetailRow
                                      label="Created"
                                      value={formatDateTime(activity.createdAt) || '-'}
                                    />
                                    </Box>
                                </Box>

                                <Box
                                  display="flex"
                                  // justifyContent="space-between"
                                  alignItems="flex-start"
                                >
                                  <Box flex={1}>
                                    
                                    <DetailRow
                                      label="Notes Summary"
                                      value={activity.summary}
                                    />
                                  <DetailRow
                                    label="Schedule Date"
                                    value={  formatDateTime(activity.date)}
                                  />
                                  </Box>
                                  
                                </Box>

                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  mt={0}
                                  
                                >
                                  <Box display="flex" alignItems="center" gap={1}>
                                    {/* Timeline action buttons based on activity type */}
                                    {activity.type === 'storeVisit' &&
                                      // Only show actions when the current detailed stage is still storeVisit
                                      // and its sub-status is not already "completed".
                                      lead?.detailedStage === 'storeVisit' &&
                                      lead.stageSubStatus !== 'completed' && (
                                        <>
                                          <Button
                                            variant="outlined"
                                            size="small"
                                            sx={{ textTransform: 'none', fontSize: 11 }}
                                            onClick={() =>
                                              applyStageUpdateFromTimeline({
                                                detailedStage: 'storeVisit',
                                                stageSubStatus: 'completed',
                                              })
                                            }
                                          >
                                            Mark Store Visit Completed
                                          </Button>
                                          <Button
                                            variant="outlined"
                                            size="small"
                                            sx={{ textTransform: 'none', fontSize: 11 }}
                                            onClick={() => {
                                              setRescheduleType('storeVisit')
                                              setIsCreateContactModalOpen(true)
                                            }}
                                          >
                                            Reschedule
                                          </Button>
                                        </>
                                      )}
                                    {activity.type === 'siteVisit' &&
                                      // Show actions for site visit when it is the current stage
                                      // and sub-status is still 'scheduled' (not completed).
                                      lead?.detailedStage === 'siteVisit' &&
                                      lead.stageSubStatus !== 'completed' && (
                                        <>
                                          <Button
                                            variant="outlined"
                                            size="small"
                                            sx={{ textTransform: 'none', fontSize: 11 }}
                                            onClick={() =>
                                              applyStageUpdateFromTimeline({
                                                detailedStage: 'siteVisit',
                                                stageSubStatus: 'completed',
                                              })
                                            }
                                          >
                                            Mark Site Visit Completed
                                          </Button>
                                          <Button
                                            variant="outlined"
                                            size="small"
                                            sx={{ textTransform: 'none', fontSize: 11 }}
                                            onClick={() => {
                                              setRescheduleType('siteVisit')
                                              setIsCreateContactModalOpen(true)
                                            }}
                                          >
                                            Reschedule
                                          </Button>
                                        </>
                                      )}
                                    {activity.channel === 'Quotation Sent' &&
                                      // Hide button if:
                                      // 1. Quotation is already approved, OR
                                      // 2. Lead has moved to terminal stage (won/lost/hold)
                                      !(
                                        (lead?.detailedStage === 'quotation' && lead.stageSubStatus === 'approved') ||
                                        lead?.detailedStage === 'won' ||
                                        lead?.detailedStage === 'lost' ||
                                        lead?.detailedStage === 'hold'
                                      ) && (
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          sx={{ textTransform: 'none', fontSize: 11 }}
                                          onClick={() =>
                                            applyStageUpdateFromTimeline({
                                              detailedStage: 'quotation',
                                              stageSubStatus: 'approved',
                                            })
                                          }
                                        >
                                          Mark Quotation Approved
                                        </Button>
                                      )}

                                    {/* <Avatar
                                      sx={{
                                        width: 24,
                                        height: 24,
                                        fontSize: 10,
                                        bgcolor: 'grey.300',
                                      }}
                                    >
                                      {activity.ownerInitials}
                                    </Avatar>
                                    <Typography fontSize={12} color="text.secondary">
                                      {activity.owner}
                                    </Typography> */}
                                  </Box>
                                </Box>
                              </Box>
                            </Card>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Appointments Tab */}
            {false && (
              <Box>
                {!lead?.appointments || lead?.appointments?.length === 0 ? (
                  <Card>
                    <CardContent>
                      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <Typography color="text.secondary">
                          No appointments available
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ) : (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(2, 1fr)',
                      },
                      gap: 3,
                      alignItems: 'stretch'
                    }}
                  >
                    {lead?.appointments?.map((appointment) => {
                      // Format date display
                      const appointmentDate = new Date(appointment?.date);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const tomorrow = new Date(today);
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      const appointmentDateOnly = new Date(appointmentDate);
                      appointmentDateOnly.setHours(0, 0, 0, 0);

                      let dateDisplay = '';
                      if (appointmentDateOnly.getTime() === today.getTime()) {
                        dateDisplay = `Today, ${appointmentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                      } else if (appointmentDateOnly.getTime() === tomorrow.getTime()) {
                        dateDisplay = `Tomorrow, ${appointmentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                      } else {
                        dateDisplay = appointmentDate.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        });
                      }

                      // Get status color and label
                      const getStatusConfig = () => {
                        switch (appointment.status) {
                          case 'completed':
                            return { color: 'success' as const, label: 'COMPLETED' };
                          case 'scheduled':
                            return { color: 'primary' as const, label: 'SCHEDULED' };
                          case 'no-show':
                            return { color: 'error' as const, label: 'NO-SHOW' };
                          case 'cancelled':
                            return { color: 'default' as const, label: 'CANCELLED' };
                          default:
                            return { color: 'default' as const, label: 'SCHEDULED' };
                        }
                      };

                      const statusConfig = getStatusConfig();

                      // Get type icon and display name
                      const getTypeConfig = () => {
                        switch (appointment.type) {
                          case 'showroom':
                            return { icon: <StorefrontIcon />, label: 'Showroom Visit' };
                          case 'site-measurement':
                            return { icon: <StraightenIcon />, label: 'Site Measurement' };
                          case 'discovery-call':
                            return { icon: <PhoneIcon />, label: 'Discovery Call' };
                          case 'site':
                            return { icon: <CalendarTodayIcon />, label: 'Site Visit' };
                          case 'virtual':
                            return { icon: <PhoneIcon />, label: 'Virtual Meeting' };
                          default:
                            return { icon: <CalendarTodayIcon />, label: appointment.title };
                        }
                      };

                      const typeConfig = getTypeConfig();

                      return (
                        <Box
                          key={appointment.id}
                          sx={{
                            display: 'flex',
                            minWidth: 0, // Prevents flex items from overflowing
                          }}
                        >
                          <Card
                            variant="outlined"
                            sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              borderRadius: 2,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                              }
                            }}
                          >
                            <CardContent sx={{ flexGrow: 1, p: 2, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                              {/* Header Section - Icon, Title, ID, and Status */}
                              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2.5}>
                                <Box display="flex" alignItems="flex-start" gap={2} flex={1}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: 30,
                                      height: 30,
                                      borderRadius: '50%',
                                      bgcolor: statusConfig.color === 'success'
                                        ? 'success.light'
                                        : statusConfig.color === 'primary'
                                          ? 'primary.light'
                                          : statusConfig.color === 'error'
                                            ? 'error.light'
                                            : 'grey.200',
                                      color: statusConfig.color === 'success'
                                        ? 'success.main'
                                        : statusConfig.color === 'primary'
                                          ? 'primary.main'
                                          : statusConfig.color === 'error'
                                            ? 'error.main'
                                            : 'grey.600',
                                      flexShrink: 0,
                                      '& svg': {
                                        fontSize: 24,
                                      }
                                    }}
                                  >
                                    {typeConfig.icon}
                                  </Box>
                                  <Box flex={1} minWidth={0}>
                                    <Typography fontWeight={600} fontSize={16} color="text.primary" mb={0.5}>
                                      {typeConfig.label}
                                    </Typography>
                                    {appointment.appointmentId && (
                                      <Typography fontSize={14} color="text.secondary">
                                        {appointment.appointmentId}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                                <Chip
                                  label={statusConfig.label}
                                  size="small"
                                  color={statusConfig.color}
                                  sx={{
                                    height: 24,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    ml: 1,
                                    flexShrink: 0,
                                  }}
                                />
                              </Box>

                              {/* Date & Time - Single line with bullet separator */}
                              <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                                <CalendarTodayIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                <Typography fontSize={15} color="text.secondary">
                                  {dateDisplay} • {appointment.time}
                                </Typography>
                              </Box>

                              {/* Location */}
                              <Box display="flex" alignItems="flex-start" gap={1.5} mb={2}>
                                <LocationOnIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.25, flexShrink: 0 }} />
                                <Typography fontSize={15} color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                  {appointment.location}
                                </Typography>
                              </Box>

                              {/* Assigned To - with Person Icon and Role Badge */}
                              {appointment.assignedTo && (
                                <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                                  <PersonIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                                  <Box display="flex" minWidth={0} gap={1}>
                                    <Typography fontSize={15} fontWeight={500} color="text.primary" noWrap>
                                      {appointment.assignedTo}
                                    </Typography>
                                    {appointment.assignedToRole && (
                                      <Chip
                                        label={appointment.assignedToRole}
                                        size="small"
                                        sx={{
                                          height: 20,
                                          fontSize: 10,
                                          fontWeight: 500,
                                          mt: 0.5,
                                          bgcolor: 'grey.100',
                                          color: 'text.secondary',
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                              )}

                              {/* Details Section - with List Icon */}
                              {appointment.details && (
                                <Box display="flex" alignItems="flex-start" gap={1.5} mb={2}>
                                  <DescriptionIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.25, flexShrink: 0 }} />
                                  <Typography fontSize={14} color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                    {appointment.details}
                                  </Typography>
                                </Box>
                              )}

                              {/* Action Buttons/Links Section - Fixed at bottom */}
                              <Box
                                display="flex"
                                gap={1.5}
                                flexWrap="wrap"
                                mt="auto"
                                pt={2.5}
                                sx={{
                                  borderTop: '1px solid',
                                  borderColor: 'divider',
                                  flexShrink: 0,
                                  width: '100%',
                                }}
                              >
                                {appointment.status === 'scheduled' && (
                                  <>
                                    <Button
                                      variant="contained"
                                      size="medium"
                                      sx={{
                                        fontSize: 14,
                                        py: 1,
                                        px: 1,
                                        textTransform: 'none',
                                        flex: 1,
                                        minWidth: 0,
                                      }}
                                    >
                                      Confirm Attendance
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      size="medium"
                                      sx={{
                                        fontSize: 14,
                                        py: 1,
                                        px: 1,
                                        textTransform: 'none',
                                        flex: 1,
                                        minWidth: 0,
                                      }}
                                    >
                                      Reschedule
                                    </Button>
                                  </>
                                )}
                                {appointment.status === 'completed' && (
                                  <>
                                    <Button
                                      variant="contained"
                                      size="medium"
                                      sx={{
                                        fontSize: 14,
                                        py: 1,
                                        px: 1,
                                        textTransform: 'none',
                                        flex: 1,
                                        minWidth: 0,
                                      }}
                                    >
                                      View Summary
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      size="medium"
                                      sx={{
                                        fontSize: 14,
                                        py: 1,
                                        px: 1,
                                        textTransform: 'none',
                                        flex: 1,
                                        minWidth: 0,
                                      }}
                                    >
                                      Reschedule
                                    </Button>
                                  </>
                                )}
                                {appointment.status === 'no-show' && (
                                  <Button
                                    variant="outlined"
                                    size="medium"
                                    sx={{
                                      fontSize: 14,
                                      py: 1,
                                      px: 1,
                                      textTransform: 'none',
                                      flex: 1,
                                      minWidth: 0,
                                    }}
                                  >
                                    Reschedule
                                  </Button>
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}

            {/* Measurements Tab */}
            {false && (
              <Stack spacing={3}>
                {!lead?.measurements || lead?.measurements?.length === 0 ? (
                  <Card>
                    <CardContent>
                      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <Typography color="text.secondary">
                          No measurements available
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {/* Section Header */}
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Box>
                        <Typography variant="h5" fontWeight={700} mb={0.5}>
                          Door Measurements
                        </Typography>
                        {lead?.measurements?.[0]?.surveyDate && (
                          <Typography fontSize={14} color="text.secondary">
                            Collected during site survey on{' '}
                            {lead?.measurements?.[0]?.surveyDate

                              ? new Date(lead?.measurements?.[0]?.surveyDate as string).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })
                              : ''}
                          </Typography>

                        )}
                      </Box>
                      <Button
                        variant="outlined"
                        startIcon={<PictureAsPdfIcon />}
                        sx={{ minWidth: 200 }}
                      >
                        Download Measurement Report
                      </Button>
                    </Box>

                    {/* Measurement Cards */}
                    <Stack spacing={3}>
                      {lead?.measurements?.map((measurement: LeadMeasurement) => (
                        <Card key={measurement.id} variant="outlined">
                          <Box display="flex" flexDirection="row" gap={2} p={2}>
                            <Box display="flex" gap={2} mb={2} >
                              {/* Identifier */}
                              <Box
                                sx={{
                                  width: 48,
                                  height: 48,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: 'primary.light',
                                  color: 'primary.main',
                                  borderRadius: 1,
                                  fontWeight: 600,
                                  fontSize: 18,
                                }}
                              >
                                {measurement.identifier}
                              </Box>
                              <Box flex={1}>
                                <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                                  <Typography variant="h6" fontWeight={600}>
                                    {measurement.doorName}
                                  </Typography>
                                  <Chip
                                    label={measurement.locationTag}
                                    size="small"
                                    color={measurement.locationTagColor || 'primary'}
                                    sx={{
                                      height: 22,
                                      fontSize: 11,
                                      fontWeight: 600,
                                      textTransform: 'uppercase',
                                    }}
                                  />
                                </Box>

                                {/* Dimensions */}
                                <Typography fontSize={13} color="text.secondary" mb={0.5}>
                                  <strong>DIMENSIONS (H X W X D):</strong>{' '}
                                  {measurement.dimensions.height} x {measurement.dimensions.width} x{' '}
                                  {measurement.dimensions.depth}
                                </Typography>

                                {/* Frame Type */}
                                <Typography fontSize={13} color="text.secondary" mb={0.5}>
                                  <strong>FRAME TYPE:</strong> {measurement.frameType}
                                </Typography>

                                {/* Threshold Type or Glazing */}
                                {measurement.thresholdType && (
                                  <Typography fontSize={13} color="text.secondary" mb={0.5}>
                                    <strong>THRESHOLD TYPE:</strong> {measurement.thresholdType}
                                  </Typography>
                                )}
                                {measurement.glazing && (
                                  <Typography fontSize={13} color="text.secondary" mb={0.5}>
                                    <strong>GLAZING:</strong> {measurement.glazing}
                                  </Typography>
                                )}
                              </Box>
                            </Box>

                            {/* Site Photos */}
                            <Box mb={2}>
                              <Typography fontSize={12} fontWeight={600} textTransform="uppercase" color="text.secondary" mb={1}>
                                SITE PHOTOS:
                              </Typography>
                              <Box display="flex" gap={1.5}>
                                {measurement.sitePhotos && measurement.sitePhotos.length > 0 ? (
                                  measurement.sitePhotos.map((photo: MeasurementPhoto, index: number) => (
                                    <Box
                                      key={photo.id}
                                      sx={{
                                        width: 80,
                                        height: 80,
                                        borderRadius: 1,
                                        bgcolor: photo.url ? 'grey.200' : 'grey.100',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        overflow: 'hidden',
                                        position: 'relative',
                                        flexShrink: 0,
                                      }}
                                    >
                                      {photo.url ? (
                                        <Box
                                          sx={{
                                            width: '100%',
                                            height: '100%',
                                            bgcolor: index % 2 === 0 ? 'warning.light' : 'info.light',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                          }}
                                        >
                                          <Typography fontSize={10} color="text.secondary">
                                            Photo {index + 1}
                                          </Typography>
                                        </Box>
                                      ) : (
                                        <CameraAltIcon sx={{ fontSize: 28, color: 'grey.400' }} />
                                      )}
                                    </Box>
                                  ))
                                ) : (
                                  <Typography fontSize={12} color="text.secondary">
                                    No photos available
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </Card>
                      ))}
                    </Stack>

                    {/* Technical Notes Section */}
                    {lead?.technicalNotes && (
                      <Card variant="outlined">
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <PeopleIcon fontSize="small" color="primary" />
                              <Typography fontSize={13} fontWeight={600} textTransform="uppercase">
                                TECHNICAL NOTES & SURVEYOR COMMENTS
                              </Typography>
                            </Box>
                            <Link
                              component="button"
                              variant="body2"
                              sx={{
                                cursor: 'pointer',
                                textDecoration: 'none',
                                color: 'primary.main',
                                fontSize: 12,
                                fontWeight: 500,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                              onClick={() => { }}
                            >
                              <EditIcon sx={{ fontSize: 14 }} />
                              Update Technical Notes
                            </Link>
                          </Box>

                          <Stack spacing={2}>
                            {lead?.technicalNotes?.wallCondition && (
                              <Box>
                                <Typography fontSize={12} fontWeight={600} textTransform="uppercase" color="text.secondary" mb={0.5}>
                                  Wall Condition:
                                </Typography>
                                <Typography fontSize={13} color="text.secondary">
                                  {lead?.technicalNotes?.wallCondition}
                                </Typography>
                              </Box>
                            )}

                            {lead?.technicalNotes?.accessNotes && (
                              <Box>
                                <Typography fontSize={12} fontWeight={600} textTransform="uppercase" color="text.secondary" mb={0.5}>
                                  Access Notes:
                                </Typography>
                                <Typography fontSize={13} color="text.secondary">
                                  {lead?.technicalNotes?.accessNotes}
                                </Typography>
                              </Box>
                            )}

                            {lead?.technicalNotes?.safetyAndRegs && (
                              <Box>
                                <Typography fontSize={12} fontWeight={600} textTransform="uppercase" color="text.secondary" mb={0.5}>
                                  Safety & Regs:
                                </Typography>
                                <Typography fontSize={13} color="text.secondary">
                                  {lead?.technicalNotes?.safetyAndRegs}
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}
              </Stack>
            )}

            {/* Quotations Tab */}
            {false && (
              <Stack spacing={3}>
                <Card>
                  <CardContent>
                    {/* Header */}
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
                      <Box>
                        <Typography variant="h6" fontWeight={600} mb={0.5}>
                          Quotations History
                        </Typography>
                        <Typography fontSize={14} color="text.secondary">
                          Manage and track all estimates provided to the client.
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        sx={{ minWidth: 180 }}
                      >
                        Create New Quotation
                      </Button>
                    </Box>

                    {/* Quotations Table */}
                    {!lead?.quotations || lead?.quotations?.length === 0 ? (
                      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <Typography color="text.secondary">
                          No quotations available
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase' }}>
                                  QUOTE ID
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase' }}>
                                  DATE
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase' }}>
                                  VERSION
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase' }}>
                                  TOTAL AMOUNT
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase' }}>
                                  STATUS
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase' }}>
                                  ACTIONS
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {lead?.quotations?.map((quotation: LeadQuotation) => {
                                const quotationDate = new Date(quotation.date);
                                const formattedDate = quotationDate.toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                });

                                // Get status color and dot
                                const getStatusConfig = () => {
                                  switch (quotation.status) {
                                    case 'sent':
                                      return {
                                        color: 'primary' as const,
                                        label: 'SENT',
                                        dotColor: 'primary.main'
                                      };
                                    case 'draft':
                                      return {
                                        color: 'default' as const,
                                        label: 'DRAFT',
                                        dotColor: 'grey.500'
                                      };
                                    case 'accepted':
                                      return {
                                        color: 'success' as const,
                                        label: 'ACCEPTED',
                                        dotColor: 'success.main'
                                      };
                                    case 'rejected':
                                      return {
                                        color: 'error' as const,
                                        label: 'REJECTED',
                                        dotColor: 'error.main'
                                      };
                                    default:
                                      return {
                                        color: 'default' as const,
                                        label: 'DRAFT',
                                        dotColor: 'grey.500'
                                      };
                                  }
                                };

                                const statusConfig = getStatusConfig();

                                return (
                                  <TableRow
                                    key={quotation.id}
                                    hover
                                    sx={{
                                      cursor: 'pointer',
                                      '&:hover': {
                                        bgcolor: 'action.hover',
                                      }
                                    }}
                                    onClick={() => navigate(`/quotation-details/${quotation.id}`)}
                                  >
                                    <TableCell>
                                      <Link
                                        component="button"
                                        variant="body2"
                                        sx={{
                                          cursor: 'pointer',
                                          textDecoration: 'none',
                                          color: 'primary.main',
                                          fontWeight: 500,
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          navigate(`/quotation-details/${quotation.id}`)
                                        }}
                                      >
                                        {quotation.quoteId}
                                      </Link>
                                    </TableCell>
                                    <TableCell>
                                      <Typography fontSize={14}>
                                        {formattedDate}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={quotation.version}
                                        size="small"
                                        sx={{
                                          bgcolor: 'grey.100',
                                          color: 'text.secondary',
                                          fontSize: 12,
                                          height: 24,
                                          fontWeight: 500,
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Typography fontSize={14} fontWeight={500}>
                                        {quotation.amount}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Box display="flex" alignItems="center" gap={1}>
                                        <Box
                                          sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            bgcolor: statusConfig.dotColor,
                                          }}
                                        />
                                        <Typography fontSize={14} color="text.secondary">
                                          {statusConfig.label}
                                        </Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Box display="flex" alignItems="center" gap={0.5} onClick={(e) => e.stopPropagation()}>
                                        <Tooltip title="Download PDF">
                                          <IconButton size="small" onClick={() => { }}>
                                            <PictureAsPdfIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Send/Share">
                                          <IconButton size="small" onClick={() => { }}>
                                            <SendIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="More options">
                                          <IconButton size="small" onClick={() => { }}>
                                            <MoreVertIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        {/* Information Box */}
                        <Box
                          sx={{
                            mt: 3,
                            p: 2,
                            bgcolor: 'info.light',
                            borderRadius: 1,
                            display: 'flex',
                            gap: 1.5,
                            alignItems: 'flex-start',
                          }}
                        >
                          <InfoIcon sx={{ color: 'primary.main', fontSize: 20, mt: 0.5 }} />
                          <Typography fontSize={13} color="text.secondary">
                            Draft quotes are only visible internally. Once a quote is "Sent", the client can view it via their portal. "Accepted" quotes can be converted into a production order by the administration team.
                          </Typography>
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Stack>
            )}
          </Grid>

          {/* Right Section */}
          <Grid size={4}>
            <Stack spacing={2}>
              {/* Stage Update Section */}
              {/* {lead && (
              <StageUpdateSection
                lead={lead}
                onUpdate={(updatedLead) => {
                  setLead(updatedLead)
                }}
              />
            )} */}

              {/* Quick Actions */}
              {/* <Card>
              <CardContent>
                <Typography fontWeight={600} mb={2}>
                  Quick Actions
                </Typography>

                <Stack spacing={1}>
                  <Button
                    variant="contained"
                    startIcon={<StraightenIcon />}
                    fullWidth
                    onClick={() => setIsScheduleAppointmentModalOpen(true)}
                  >
                    Schedule Measurement
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<PhoneIcon />}
                    fullWidth
                    onClick={() => setIsCreateContactModalOpen(true)}
                  >
                    Log Follow-up
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<DescriptionIcon />}
                    fullWidth
                  >
                    Generate Quote
                  </Button>
                </Stack>
              </CardContent>
            </Card> */}

              {/* Activity Feed */}
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography fontWeight={600}>
                      Activity Feed
                    </Typography>
                    <Link
                      component="button"
                      variant="body2"
                      sx={{ cursor: 'pointer', textDecoration: 'none' }}
                      onClick={() => { }}
                    >
                      VIEW ALL
                    </Link>
                  </Box>

                  <Timeline sx={{
                    [`& .${timelineItemClasses.root}:before`]: {
                      flex: 0,
                      padding: 0,
                    },
                  }}>
                    {getActivityFeedItems().map((activity, index, array) => (
                      <TimelineItem key={index}>
                        <TimelineSeparator>
                          <TimelineDot color={activity.color}>
                            {activity.icon}
                          </TimelineDot>
                          {index < array.length - 1 && <TimelineConnector />}
                        </TimelineSeparator>
                        <TimelineContent>
                          <Typography fontSize={14} fontWeight={500}>
                            {activity.title}
                          </Typography>
                          <Typography fontSize={12} color="text.secondary">
                            {activity.time.split(',')[0]}
                          </Typography>
                        </TimelineContent>
                      </TimelineItem>
                    ))}
                  </Timeline>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>

        {/* Followup Modal */}
        <FollowupModal
          isOpen={isFollowupModalOpen}
          onRequestClose={() => {
            setIsFollowupModalOpen(false)
          }}
          onSuccess={() => {
            setIsFollowupModalOpen(false)
          }}
          leadId={id}
        />

        {/* Schedule Appointment Modal */}
        <ScheduleAppointmentModal
          isOpen={isScheduleAppointmentModalOpen}
          onRequestClose={() => {
            setIsScheduleAppointmentModalOpen(false)
          }}
          onSuccess={() => {
            setIsScheduleAppointmentModalOpen(false)
          }}
          leadId={id}
          customer={
            lead
              ? {
                id: lead.id || '',
                name: lead.contact?.name || '',
                email: lead.contact?.email || '',
                phone: lead.contact?.phone || '',
                initials: lead.contact?.name
                  ? lead.contact.name
                    .split(' ')
                    .map((n: string) => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)
                  : undefined,
              }
              : undefined
          }
        />

        {/* Edit Lead Modal */}
        <AddLeadModal
          isOpen={isEditLeadModalOpen}
          onRequestClose={() => {
            setIsEditLeadModalOpen(false)
          }}
          initialData={lead || undefined}
          mode="lead"
        />

        {/* Create Contact Modal */}
        <CreateContactModal
          isOpen={isCreateContactModalOpen}
          onRequestClose={() => {
            setIsCreateContactModalOpen(false)
            setRescheduleType(null)
          }}
          leadId={id}
          currentStage={lead?.detailedStage || 'new'}
          currentStageSubStatus={lead?.stageSubStatus}
          followUpHistory={lead?.followUpHistory?.map(activity => ({
            type: activity.type,
            channel: activity.channel,
            date: activity.date,
            outcome: activity.outcome,
          }))}
          initialInteractionType={rescheduleType || undefined}
          initialNextAction={rescheduleType || undefined}
          rescheduleMode={!!rescheduleType}
          onSuccess={(payload) => {
            handleFollowUpEntry(payload)
            setIsCreateContactModalOpen(false)
            setRescheduleType(null)
          }}
        />

      </Box>
    );
  };

  return (
    <Box sx={{ flex: 1, padding: '2rem', overflowY: 'auto', bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {renderContent()}
    </Box>
  )
}
